'use client';

import { useCallback } from 'react';
import Empty from '@/components/ui-feat/empty';
import CouponCard from '@/features/reward/modules/backpack/components/coupon-card';
import emptyImg from '@/features/reward/modules/images/empty.png';
import { getBagItems } from '@/features/reward/services/backpack';
import usePagination from '@/features/casino/hooks/use-pagination';
import { buildUIItem } from '@/features/reward/modules/backpack/adapter';
import { Spin } from '@/components/ui-feat/spin';
import { useTranslations } from 'next-intl';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import IconFont from '@/components/ui/iconFont';
import InfiniteScroll from '@/components/ui-feat/infinite-scroll';
import { useAppSafearea } from '@/hooks/use-app-safearea';
import type { TabOption } from '../backpack/components/secondary-tabs';

interface UsedDrawerProps {
  tabs: TabOption[];
}

const UsedDrawer = NiceModal.create<UsedDrawerProps>((props) => {
  const { tabs } = props;
  const t = useTranslations('reward');
  const commonT = useTranslations('common');
  const modal = useModal();
  const { topSafe } = useAppSafearea();

  const loadItems = useCallback(
    async ({ current, pageSize }: { current: number; pageSize: number; }) => {
      const res = await getBagItems({
        status: '3',
        page: String(current),
        page_size: String(pageSize),
      });
      return {
        list: (res?.list ?? []).map(buildUIItem),
        total: res?.total,
        nextPage: current + 1,
      };
    },
    [],
  );

  const { list, loadMore, isFirstLoad, isEmpty, hasMore } = usePagination(loadItems);

  return (
    <Drawer open={modal.visible} onOpenChange={(open) => !open && hideUsedDrawer()}>
      <DrawerContent className="bg-dark border-none rounded-t-[10px] text-xs text-tertiary h-[100vh] font-medium flex flex-col data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 duration-200" style={{ transition: 'none' }}>
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ paddingTop: `${topSafe + 27}px` }}>
          <button
            className="tap-target-34 active:scale-90 border-none"
            onClick={hideUsedDrawer}
          >
            <IconFont size={15} color='#a9b5cb' name='arrow-back' />
          </button>

          <h1 className="text-base text-primary font-semibold">{t('invalidItems')}</h1>

          <div className="w-8"></div>
        </div>
        {isFirstLoad ? (
          <div className="flex-1 flex items-center justify-center">
            <Spin className='h-full w-full' />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto no-scrollbar px-3 py-5">
            {isEmpty && (
              <Empty
                image={emptyImg}
                imageSize={{ width: 62, height: 62 }}
                className='h-full'
                tip={
                  <div className="text-center text-[#586272] text-xs pt-3">
                    <div>{t('noItems')}</div>
                  </div>
                }
              />
            )}
            <div className="space-y-2.5">
              {!isEmpty && list.map((item) => (
                <CouponCard className="w-full" key={item.item_id} item={item} tabs={tabs} />
              ))}
              <InfiniteScroll
                hasMore={hasMore}
                loadMore={loadMore}
                noMoreText={list?.length > 8 ? commonT('noMoreData') : ''}
              />
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
});

export default UsedDrawer;

export const showUsedDrawer = (props?: UsedDrawerProps) => {
  NiceModal.register('user-drawer', UsedDrawer);
  return NiceModal.show('user-drawer', props);
};

export const hideUsedDrawer = () => {
  return NiceModal.remove('user-drawer');
};